import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, EmptyState, FormSelect, QueryStateBoundary } from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { useClassSections, useTerms } from "@/api/hooks/useClassRegistration";
import { AGE_BAND_LABELS, type ClassSection } from "@/api/types/classRegistration";
import { formatCapacity, formatWeekdaySlot, sectionDisplayName } from "./classFormat";
import { ProgramFormDialog } from "./ProgramFormDialog";
import { TermFormDialog } from "./TermFormDialog";
import { SectionFormDialog } from "./SectionFormDialog";

/**
 * Classes — term picker + the term's sections, with create actions for
 * programs/terms/sections. Mirrors features/memberships/MembersPage.tsx's
 * layout (header + action button + table), adapted for a term-scoped
 * list instead of a flat one.
 */
export function ClassesPage() {
  const { data: terms, isLoading: termsLoading, error: termsError } = useTerms(false);

  const [termId, setTermId] = useState<string>("");
  const [programOpen, setProgramOpen] = useState(false);
  const [termOpen, setTermOpen] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(false);

  // Default to the first active term once terms load; never override a
  // choice the admin has already made.
  useEffect(() => {
    if (termId || !terms || terms.length === 0) return;
    const firstActive = terms.find((t) => t.active);
    setTermId((firstActive ?? terms[0]).id);
  }, [terms, termId]);

  const {
    data: sections,
    isLoading: sectionsLoading,
    error: sectionsError,
    refetch: refetchSections,
  } = useClassSections({ term_id: termId || undefined });

  const sortedSections = useMemo(
    () => (sections ?? []).slice().sort((a, b) => a.weekday.localeCompare(b.weekday) || a.slot.localeCompare(b.slot)),
    [sections],
  );

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Classes</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => setProgramOpen(true)} data-testid="new-program-button">
            New program
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setTermOpen(true)} data-testid="new-term-button">
            New term
          </Button>
          <Button size="sm" onClick={() => setSectionOpen(true)} disabled={!termId} data-testid="new-section-button">
            New section
          </Button>
        </div>
      </div>

      <QueryStateBoundary isLoading={termsLoading} error={termsError}>
        {!terms || terms.length === 0 ? (
          <EmptyState
            title="No terms yet"
            description="Create a term before adding classes."
            action={{ label: "New term", onClick: () => setTermOpen(true) }}
          />
        ) : (
          <>
            <FormSelect
              label="Term"
              value={termId}
              onChange={(e) => setTermId(e.target.value)}
              wrapperClassName="mb-4 max-w-xs"
            >
              {terms.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {!t.active ? " (inactive)" : ""}
                </option>
              ))}
            </FormSelect>

            <QueryStateBoundary isLoading={sectionsLoading} error={sectionsError} onRetry={() => void refetchSections()}>
              {sortedSections.length === 0 ? (
                <EmptyState
                  title="No classes in this term yet"
                  description="Add a section to open instruction for this term."
                  action={{ label: "New section", onClick: () => setSectionOpen(true) }}
                />
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border bg-bg-card">
                  <table className="w-full text-sm" data-testid="sections-table">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase text-text-muted">
                        <th className="px-4 py-2">Class</th>
                        <th className="px-4 py-2">When</th>
                        <th className="px-4 py-2">Age band</th>
                        <th className="px-4 py-2">Price</th>
                        <th className="px-4 py-2">Enrolled</th>
                        <th className="px-4 py-2">Waitlist</th>
                        <th className="px-4 py-2" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {sortedSections.map((section) => (
                        <SectionRow key={section.id} section={section} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </QueryStateBoundary>
          </>
        )}
      </QueryStateBoundary>

      <ProgramFormDialog open={programOpen} onClose={() => setProgramOpen(false)} />
      <TermFormDialog open={termOpen} onClose={() => setTermOpen(false)} onCreated={(id) => setTermId(id)} />
      <SectionFormDialog open={sectionOpen} onClose={() => setSectionOpen(false)} defaultTermId={termId} />
    </div>
  );
}

function SectionRow({ section }: { section: ClassSection }) {
  return (
    <tr className="hover:bg-bg-hover" data-testid={`section-row-${section.id}`}>
      <td className="px-4 py-3">
        <Link to={`/classes/sections/${section.id}`} className="font-medium text-text-primary hover:underline">
          {sectionDisplayName(section)}
        </Link>
        {!section.active && (
          <Badge variant="secondary" size="sm" className="ml-2">
            Inactive
          </Badge>
        )}
      </td>
      <td className="px-4 py-3 text-text-secondary">{formatWeekdaySlot(section.weekday, section.slot)}</td>
      <td className="px-4 py-3 text-text-secondary">{section.age_band ? AGE_BAND_LABELS[section.age_band] : "—"}</td>
      <td className="px-4 py-3 text-text-secondary">{centsToUsd(section.price_cents_monthly)}/mo</td>
      <td className="px-4 py-3 text-text-secondary">
        <div className="flex items-center gap-2">
          {formatCapacity(section.active_enrollment_count, section.cap)}
          {section.below_minimum && (
            <Badge variant="warning" size="sm" data-testid={`below-minimum-${section.id}`}>
              Below minimum
            </Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-text-secondary">{section.waitlisted_count}</td>
      <td className="px-4 py-3 text-right">
        <Link to={`/classes/sections/${section.id}`} className="text-sm text-primary hover:underline">
          View roster
        </Link>
      </td>
    </tr>
  );
}
